import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { getSurvey } from '../../store/reducers/actionPermission';
import SearchInput from '../ReusableComponents/SearchInput';
import TmFilter from '../ReusableComponents/TmFilter';
import TmMasterTable from '../ReusableComponents/TmMasterTable';
import Loader from '../ReusableComponents/Loader';
import AntdModalComp from '../ReusableComponents/AntdModalComp';
import { DeletePopup } from '../ReusableComponents/DeletePopup';
import { toastError, toastSuccess } from '../Toast/ToastComponent';
import { useAuth } from '../../Context/AuthContext';
import SurveyApi from '../../Api/SurveyApi';
import { approveTable } from '../../constant/tableHeader';

const filterArray = [
  {
    options: [
      { value: 'Pending', label: 'Pending' },
      { value: 'Approved', label: 'Approved' },
      { value: 'Rejected', label: 'Rejected' },
    ],
    placeholder: 'Status',
    controlName: 'status',
    input: 'select',
    allowClear: true,
    size: 'small',
    style: { width: 150 },
    col: 2,
  },
  {
    order: 1,
    col: 'auto',
    className: 'ms-auto pe-0',
    input: 'button',
    type: 'dashed',
    htmlType: 'button',
    text: 'Reset',
  },
];
const ApprovalsComp = () => {
  const { authState } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const permission = useSelector(getSurvey);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(false);
  const [modalHeader, setModalHeader] = useState('');
  const debounceTimeoutRef = useRef(null);
  const hasFetched = useRef(false);
  const [filterArr, setFilterArr] = useState({
    search: searchParams.get('search') || '',
    status: searchParams.get('status') || null,
    month: searchParams.get('month') ? Number(searchParams.get('month')) : '',
    year: searchParams.get('year') ? Number(searchParams.get('year')) : '',
  });
  const [buttonPermission, setButtonPermissions] = useState({
    add: false,
    edit: false,
    delete: undefined,
    view: false,
  });
  const getSurveyList = useCallback(
    async (
      searchQuery = '',
      statusQuery = '',
      monthQuery = '',
      yearQuery = '',
    ) => {
      try {
        setLoading(true);

        const payload = {
          user_id: authState.id,
          limit: 1000,
          page: 1,
          offset: 0,
          search: searchQuery || '',
          status: statusQuery || '',
          month: monthQuery || '',
          year: yearQuery || '',
        };

        const res = await SurveyApi.Approve(payload);

        if (res?.data?.success) {
          const rawData = res.data.result;
          const resultData =
            rawData && rawData.rows
              ? rawData.rows
              : Array.isArray(rawData)
                ? rawData
                : [];

          const mappedData = resultData.map((survey, index) => ({
            ...survey,
            id: survey.id,
            serialNo: index + 1,

            title: survey.title || 'No Title',

            type: survey['PeriodModel.Period_Name'] || '-',
            month: survey['PeriodModel.fromMonth'] || '-',
            questionCount: survey.question_count || '0',
            startDate: survey.start_date
              ? moment(survey.start_date).format('YYYY-MM-DD')
              : '-',
            endDate: survey.end_date
              ? moment(survey.end_date).format('YYYY-MM-DD')
              : '-',
            progress: survey.status,
            status: survey.status === 'Approved' ? 1 : 0,
            departmentsID: '1',
          }));

          setData(mappedData);
          console.log('Check this in Console:', mappedData);
        } else {
          setData([]);
        }
      } catch (err) {
        console.error('🔥 API Error:', err);
      } finally {
        setLoading(false);
      }
    },
    [authState?.id],
  );

  useEffect(() => {
    if (hasFetched.current) return;

    hasFetched.current = true;
    if (permission) {
      if (permission.checked === false) {
        navigate('/');
      } else {
        const obj = {};
        permission.actions.forEach((el) => {
          obj[el.statename.toLowerCase()] = el.checked;
        });
        setButtonPermissions(obj);
      }
    }
    getSurveyList(
      filterArr.search,
      filterArr.status,
      filterArr.month,
      filterArr.year,
    );
  }, [
    permission,
    getSurveyList,
    navigate,
    filterArr.search,
    filterArr.status,
    filterArr.month,
    filterArr.year,
  ]);

  const handleSearchChange = (value) => {
    setFilterArr((prev) => {
      const nextState = { ...prev, search: value };
      if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = setTimeout(() => {
        getSurveyList(
          nextState.search,
          nextState.status,
          nextState.month,
          nextState.year,
        );
      }, 400);
      return nextState;
    });
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        value ? next.set('search', value) : next.delete('search');
        next.set('pageNumber', 1);
        return next;
      },
      { replace: true },
    );
  };

  const handleFilterChange = (value) => {
    if (Object.keys(value).includes('submit')) {
      return;
    }
    setFilterArr((prev) => {
      const nextState = { ...prev, ...value };
      getSurveyList(
        nextState.search,
        nextState.status,
        nextState.month,
        nextState.year,
      );
      return nextState;
    });
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        Object.entries(value).forEach(([k, v]) => {
          if (v === null || v === undefined || v === '') {
            next.delete(k);
          } else {
            next.set(k, String(v));
          }
        });
        next.set('pageNumber', 1);
        return next;
      },
      { replace: true },
    );
  };

  const toggleModal = (type, value) => {
    if (type === 'Close') return setModal(false);
    if (type === 'Add') return navigate('/my-survey');

    if ((type === 'Edit' || type === 'View') && value) {
      navigate(`/aproved/${value.id}`, { state: { oldData: value } });
    }
  };

  const callDelete = async (id) => {
    await DeletePopup(async (ok) => {
      if (!ok) return;
      try {
        const res = await SurveyApi.delete(id);
        if (res.data.success) {
          toastSuccess(res.data.message);
          getSurveyList(
            filterArr.search,
            filterArr.status,
            filterArr.month,
            filterArr.year,
          );
        }
      } catch (err) {
        toastError('Delete failed');
      }
    });
  };

  return (
    <div>
      <div className="d-md-flex align-items-center mb-2">
        <h5 className="mb-0">Survey Approval </h5>
        <div className="ms-auto d-flex align-items-center">
          <SearchInput
            placeholder="Search"
            value={filterArr.search}
            onSearchChange={handleSearchChange}
          />
        </div>
      </div>

      <div className="mt-2 mb-2">
        <TmFilter
          filterArray={filterArray.map((f) => {
            if (f.controlName === 'status')
              return { ...f, defaultValue: filterArr.status };
            return f;
          })}
          handleFilterChange={handleFilterChange}
        />
      </div>

      {loading ? (
        <Loader />
      ) : (
        <TmMasterTable
          buttonPermission={buttonPermission}
          rows={data}
          columns={approveTable}
          custom={[]}
          toggleModal={toggleModal}
          handleDelete={callDelete}
        />
      )}

      <AntdModalComp
        width="60%"
        modalOpen={modal}
        toggleModal={() => toggleModal('Close')}
        modalHeader={modalHeader}
      />
    </div>
  );
};

export default ApprovalsComp;
